import { computed, type MaybeRefOrGetter, toValue } from 'vue'

import { useLocation } from '@common/location-layer/composables/useLocation'

import type { GlobalErrorPageContextInput, GlobalErrorVariant, LocationBrandingSource } from '../types/global-error.types'

export type { GlobalErrorVariant, GlobalErrorPageContextInput } from '../types/global-error.types'

const VARIANT_COPY_KEYS: Record<GlobalErrorVariant, { titleKey: string; descriptionKey: string }> = {
  notFound: {
    titleKey: 'common.errors.notFound.title',
    descriptionKey: 'common.errors.notFound.description',
  },
  forbidden: {
    titleKey: 'common.errors.forbidden.title',
    descriptionKey: 'common.errors.forbidden.description',
  },
  serverError: {
    titleKey: 'common.errors.serverError.title',
    descriptionKey: 'common.errors.serverError.description',
  },
  generic: {
    titleKey: 'common.errors.generic.title',
    descriptionKey: 'common.errors.generic.description',
  },
}

export function resolveErrorVariant(statusCode?: number): GlobalErrorVariant {
  switch (statusCode) {
    case 404:
      return 'notFound'
    case 403:
      return 'forbidden'
    case 500:
    case 502:
    case 503:
      return 'serverError'
    default:
      return 'generic'
  }
}

function readSupportEmail(locationStore: LocationBrandingSource): string | null {
  return locationStore.branding?.supportEmail || locationStore.locationData?.branding?.supportEmail || null
}

/**
 * Derives error-page context without throwing when location composable is unavailable.
 */
export function useGlobalErrorPageContext(error: MaybeRefOrGetter<GlobalErrorPageContextInput | undefined>) {
  let location: ReturnType<typeof useLocation> | null = null

  try {
    location = useLocation()
  } catch (storeError: unknown) {
    if (import.meta.dev) {
      console.warn('[GlobalErrorPageContext] Location composable unavailable — support email disabled.', storeError)
    }
  }

  const locationStore = location?.store ?? null

  const statusCode = computed(() => toValue(error)?.statusCode)

  const variant = computed(() => resolveErrorVariant(statusCode.value))

  const titleKey = computed(() => VARIANT_COPY_KEYS[variant.value].titleKey)

  const descriptionKey = computed(() => VARIANT_COPY_KEYS[variant.value].descriptionKey)

  const supportEmail = computed(() => (locationStore ? readSupportEmail(locationStore) : null))

  const showDevMeta = computed(() => import.meta.dev)

  const devStatusLabel = computed(() => {
    if (!showDevMeta.value) {
      return null
    }
    const code = statusCode.value
    return code != null ? String(code) : 'unknown'
  })

  return {
    variant,
    statusCode,
    titleKey,
    descriptionKey,
    supportEmail,
    showDevMeta,
    devStatusLabel,
  }
}
